'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslations } from 'next-intl';
import { useRef, useEffect } from 'react';

import { Shop, ShopList } from '@/api/backend/contract/Shop';
import { IconKitChevron } from '@/icon/kit/Chevron';
import { UiShopPointInfo } from '@/ui/shop-point-info';
import { UiKitButton } from '@/ui-kit/button';
import { UiKitLoader } from '@/ui-kit/loader';
import { utilPluralize } from '@/util/plularize';

import { styles } from './UiShopPointSelector.styles';

export interface UiShopPointSelectorProps {
  pointsList?: ShopList;
  onSelectPoint: (point: Shop | null) => void;
  activePointId: string | null;
  onPointConfirm: (pointId: string) => void;
  hideConfirmButton?: boolean;
  loading?: boolean;
  isFreeShipping?: boolean;
  city?: string;
  targetBlank?: boolean;
  isScroll?: boolean;
  type?: 'pdp' | 'simple';
  onLinkClick?: (params: { label: string; url: string }) => void;
}

export function UiShopPointSelector({
  onSelectPoint,
  activePointId,
  pointsList = [],
  onPointConfirm,
  hideConfirmButton = false,
  loading = false,
  isFreeShipping,
  city,
  targetBlank = false,
  isScroll = true,
  type,
  onLinkClick
}: UiShopPointSelectorProps) {
  const slots = styles({ type, isScroll });

  const t = useTranslations();

  const pointListRef = useRef<HTMLDivElement>(null);
  const count = pointsList.length;
  const pointActive = pointsList.find((item) => item.id === activePointId);

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count,
    getItemKey: (index) => pointsList[index].id,
    estimateSize: () => 80,
    getScrollElement: () => pointListRef.current
  });

  const pluralizeShops = utilPluralize({
    one: t('pluralize.shops.one'),
    few: t('pluralize.shops.few'),
    many: t('pluralize.shops.many')
  });

  const virtualizerItems = virtualizer.getVirtualItems();

  useEffect(() => {
    virtualizer.scrollToIndex(
      pointsList.findIndex((point) => point.id === activePointId),
      { align: 'start' }
    );
  }, [activePointId, pointsList, virtualizer]);

  return (
    <div className={slots.mobileContainer()}>
      <div className={slots.base()}>
        {type !== 'simple' && (
          <>
            <h2 className={slots.title()}>
              {t('availabilityOfGoods', { count })} {pluralizeShops(count)}
            </h2>

            <div className={slots.info()}>
              <UiKitButton
                color="transparent"
                className={slots.btn()}
              >
                <div className={slots.btnRow()}>
                  {city}
                  <IconKitChevron
                    width={16}
                    height={16}
                  />
                </div>
              </UiKitButton>

              <UiKitButton
                color="transparent"
                className={slots.btn()}
              >
                <div className={slots.btnRow()}>
                  <div className={slots.sizeRow()}>
                    <div className={slots.size()}>{t('Size')}:</div> 12
                  </div>
                  <IconKitChevron
                    width={16}
                    height={16}
                    className={slots.btnIcon()}
                  />
                </div>
              </UiKitButton>
            </div>
          </>
        )}

        <div
          ref={pointListRef}
          className={slots.pointsScrollWrapper()}
        >
          {isScroll && (
            <>
              {(!activePointId && targetBlank) || !targetBlank ? (
                <div
                  className={slots.pointsScrollInner()}
                  style={{
                    height: virtualizer.getTotalSize()
                  }}
                >
                  <div
                    className={slots.pointList()}
                    style={{
                      transform: `translateY(${virtualizerItems[0]?.start ?? 0}px)`
                    }}
                  >
                    {virtualizerItems.map((virtualItem) => {
                      const point = pointsList[virtualItem.index];
                      const isActive = point.id === activePointId;

                      return (
                        <div
                          key={virtualItem.key}
                          data-index={virtualItem.index}
                          ref={virtualizer.measureElement}
                          className={slots.pointItemContainer()}
                        >
                          <div
                            role="button"
                            className={slots.pointItem({ isActive })}
                            onClick={
                              type === 'pdp' ? () => onSelectPoint(point) : undefined
                            }
                          >
                            <UiShopPointInfo
                              point={point}
                              activePointId={activePointId}
                              onPointConfirm={onPointConfirm}
                              onSelectPoint={onSelectPoint}
                              hideConfirmButton={hideConfirmButton}
                              isFreeShipping={isFreeShipping}
                              isVisibleStockBtn={type === 'simple'}
                              type={type}
                              isScroll={isScroll}
                              onLinkClick={onLinkClick}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {virtualizerItems.length === 0 && !loading && (
                      <div className={slots.emptyState()}>{t('noPointsFound')}</div>
                    )}
                  </div>
                </div>
              ) : (
                ''
              )}
            </>
          )}

          {!isScroll && (
            <>
              <div className={slots.pointList()}>
                {pointsList.map((point) => {
                  const isActive = point.id === activePointId;

                  return (
                    <div
                      key={point.id}
                      className={slots.pointItemContainer()}
                    >
                      <div className={slots.pointItem({ isActive })}>
                        <UiShopPointInfo
                          point={point}
                          activePointId={activePointId}
                          onPointConfirm={onPointConfirm}
                          onSelectPoint={onSelectPoint}
                          hideConfirmButton={hideConfirmButton}
                          isFreeShipping={isFreeShipping}
                          isVisibleStockBtn
                          type={type}
                          isScroll={isScroll}
                          onLinkClick={onLinkClick}
                        />
                      </div>
                    </div>
                  );
                })}
                {pointsList.length === 0 && !loading && (
                  <div className={slots.emptyState()}>{t('noPointsFound')}</div>
                )}
              </div>
            </>
          )}

          {targetBlank && activePointId && pointActive && (
            <div className={slots.pointItem({ isActive: true })}>
              <UiShopPointInfo
                point={pointActive}
                activePointId={activePointId}
                onSelectPoint={onSelectPoint}
                onPointConfirm={onPointConfirm}
                hideConfirmButton={hideConfirmButton}
                isFreeShipping={isFreeShipping}
                type={type}
                isScroll={isScroll}
              />
            </div>
          )}
        </div>
        <UiKitLoader show={loading} />
      </div>
    </div>
  );
}
